<template>
  <v-container fluid class="claim-page">
    <!-- Alert -->
    <AAlertBanner :messages="alertError" hide-title />

    <!-- Confirmation Dialog -->
    <v-dialog v-model="paymentReceivedSubmitDialog" max-width="460" content-class="claim-confirm-dialog">
      <div class="claim-confirm-card">
        <h3 class="claim-confirm-card__title">{{ this.model.name.singular }}</h3>

        <AAlertBanner :messages="paymentReceivedError" hide-title dense />

        <p class="claim-confirm-card__message">Please confirm that Payment Received from HRDC</p>
        <div class="claim-confirm-card__actions">
          <button
            class="claim-confirm-card__btn claim-confirm-card__btn--cancel"
            @click="paymentReceivedSubmitDialog = false"
          >
            Cancel
          </button>
          <button
            class="claim-confirm-card__btn claim-confirm-card__btn--confirm"
            @click="modalPaymentReceivedSubmit()"
          >
            Submit
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- Header -->
    <div class="claim-header">
      <div class="claim-header__top">

        <div v-if="!loadingDialog" class="claim-header__info">
          <h1 class="claim-header__title">
            Payment Received Details ({{ this.formData.refundTypeDesc }})
          </h1>
        </div>

        <div class="claim-header__actions">
          <span v-if="isTaxpodAdminRoles && !btnDisabled">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <button
                  class="hero-action-btn hero-action-btn--green"
                  :disabled="apiSubmit.isLoading"
                  @click="paymentReceivedSubmitDialog = true"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon size="18">mdi-check-circle-outline</v-icon>
                </button>
              </template>
              <span>Submit</span>
            </v-tooltip>
          </span>
        </div>
      </div>
    </div>

    <div class="claim-sections">
      <!-- Special CN Case Section -->
      <div class="section-card" v-if="isSpecialCnCase">
        <div class="section-header section-header--amber">
          <v-icon color="white" small>mdi-alert-circle-outline</v-icon>
          <span class="section-header__title">Special CN Case</span>
        </div>
        <div class="section-body">
          <p class="cn-description">This is a Special CN case, CN will be generated based on below data:</p>
          <div class="cn-cards-grid">
            <div
              class="cn-card"
              v-for="(item, key) of this.specialCnData"
              :key="key"
            >
              <div class="cn-card__badge">CN {{ key + 1 }}</div>
              <div class="cn-card__details">
                <div class="cn-detail-row">
                  <span class="cn-detail-label">Client Inv No.</span>
                  <span class="cn-detail-value">{{ item["clientInvNo"] }}</span>
                </div>
                <div class="cn-detail-row">
                  <span class="cn-detail-label">HRDC Inv No.</span>
                  <span class="cn-detail-value">{{ item["hrdcInvNo"] }}</span>
                </div>
                <div class="cn-detail-row">
                  <span class="cn-detail-label">Amount</span>
                  <span class="cn-detail-value cn-detail-value--amount">RM {{ item["cnAmount"] }}</span>
                </div>
                <div class="cn-detail-row">
                  <span class="cn-detail-label">Description</span>
                  <span class="cn-detail-value" v-html="item['cnDesc']"></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Main Content -->
      <div :class="['content-grid', { 'content-grid--split': formData.refundType == 2 }]">
        <!-- 70% / Normal Case -->
        <div class="section-card">
          <div class="section-header">
            <v-icon color="white" small>mdi-cash-check</v-icon>
            <span class="section-header__title">Payment Details</span>
            <span v-if="formData.refundType == 2" class="section-header__badge">70%</span>
          </div>
          <div class="section-body">
            <!-- Claim ID -->
            <div class="form-row">
              <label class="form-label">Claim ID</label>
              <div class="form-field">
                <div class="claim-copyable" @click="copyContent(formData.claimId)">
                  <span class="claim-copyable__text">{{ formData.claimId || '-' }}</span>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="claim-copyable__icon">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9.75a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184" />
                  </svg>
                </div>
              </div>
            </div>

            <!-- Subscription Date Range -->
            <div class="form-row" v-if="isRefundCases">
              <label class="form-label">
                Subscription Period
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon small color="#f59e0b" v-bind="attrs" v-on="on" class="ml-1">mdi-information</v-icon>
                  </template>
                  <span>
                    Note: Please double confirm on the subscription period as
                    <br />
                    this will be the final date to generate for CN
                  </span>
                </v-tooltip>
              </label>
              <div class="form-field">
                <div class="date-range">
                  <v-menu
                    ref="menuSubscriptionStartDate"
                    v-model="menuSubscriptionStartDate"
                    :close-on-content-click="false"
                    :return-value.sync="date"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <div class="rv-datepicker__trigger" v-bind="attrs" v-on="on">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="rv-datepicker__icon">
                          <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
                        </svg>
                        <span v-if="formData.subscriptionStartDate" class="rv-datepicker__text">{{ formData.subscriptionStartDate }}</span>
                        <span v-else class="rv-datepicker__placeholder">Start date</span>
                      </div>
                    </template>
                    <v-date-picker
                      v-model="formData.subscriptionStartDate"
                      no-title
                      scrollable
                    >
                      <v-spacer></v-spacer>
                      <v-btn text color="primary" @click="menuSubscriptionStartDate = false">Cancel</v-btn>
                      <v-btn text color="primary" @click="$refs.menuSubscriptionStartDate.save(date)">OK</v-btn>
                    </v-date-picker>
                  </v-menu>

                  <span class="date-range__separator">to</span>

                  <v-menu
                    ref="menuSubscriptionEndDate"
                    v-model="menuSubscriptionEndDate"
                    :close-on-content-click="false"
                    :return-value.sync="date"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <div class="rv-datepicker__trigger" v-bind="attrs" v-on="on">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="rv-datepicker__icon">
                          <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
                        </svg>
                        <span v-if="formData.subscriptionEndDate" class="rv-datepicker__text">{{ formData.subscriptionEndDate }}</span>
                        <span v-else class="rv-datepicker__placeholder">End date</span>
                      </div>
                    </template>
                    <v-date-picker
                      v-model="formData.subscriptionEndDate"
                      no-title
                      scrollable
                    >
                      <v-spacer></v-spacer>
                      <v-btn text color="primary" @click="menuSubscriptionEndDate = false">Cancel</v-btn>
                      <v-btn text color="primary" @click="$refs.menuSubscriptionEndDate.save(date)">OK</v-btn>
                    </v-date-picker>
                  </v-menu>
                </div>
                <div v-if="errorField.subscriptionStartDate" class="field-error">{{ errorField.subscriptionStartDate }}</div>
                <div v-if="errorField.subscriptionEndDate" class="field-error">{{ errorField.subscriptionEndDate }}</div>
              </div>
            </div>

            <!-- HRDC Invoice No. -->
            <div class="form-row">
              <label class="form-label">HRDC Invoice No.</label>
              <div class="form-field">
                <v-text-field
                  dense
                  filled
                  v-model="formData.invoiceNo"
                  :error-messages="errorField.invoiceNo"
                  disabled
                  :class="{ 'text-decoration-line-through': isSpecialCnCase }"
                ></v-text-field>
              </div>
            </div>

            <!-- Credit Note -->
            <div class="form-row" v-if="isCnIgnored">
              <label class="form-label">Credit Note</label>
              <div class="form-field">
                <div
                  class="file-card"
                  :class="formData.creditNote.length > 0 ? 'file-card--has-file' : 'file-card--empty'"
                  @drop.prevent="addDropMultipleFile($event, 'creditNote', 'pdf.*')"
                  @dragover.prevent
                >
                  <div class="file-card__icon-wrap">
                    <div class="file-card__icon">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__svg">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                      </svg>
                    </div>
                  </div>
                  <div class="file-card__info">
                    <span class="file-card__name">{{ formData.creditNote.length > 0 ? formData.creditNote.map(f => f.name).join(', ') : 'No file selected' }}</span>
                    <span class="file-card__hint">{{ formData.creditNote.length > 0 ? formData.creditNote.length + ' file(s) selected' : 'PDF only' }}</span>
                  </div>
                  <div class="file-card__actions">
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <label class="file-card__btn" :class="{ 'file-card__btn--disabled': isSpecialCnCase }" v-on="on">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__btn-icon"><path stroke-linecap="round" stroke-linejoin="round" d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13" /></svg>
                          <input type="file" class="file-card__hidden" accept=".pdf" multiple :disabled="isSpecialCnCase" @change="handleMultiFileChange($event, 'creditNote', 'creditNoteNo')" />
                        </label>
                      </template>
                      <span>Upload</span>
                    </v-tooltip>
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <button class="file-card__btn" @click="previewAttachment('creditNote')" v-on="on">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__btn-icon"><path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" /><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /></svg>
                        </button>
                      </template>
                      <span>Preview</span>
                    </v-tooltip>
                  </div>
                </div>
                <div v-if="errorField.creditNote" class="field-error">{{ errorField.creditNote }}</div>
              </div>
            </div>

            <!-- Client/taxPOD Invoice -->
            <div class="form-row" v-if="isRefundCases">
              <label class="form-label">Client/taxPOD Invoice</label>
              <div class="form-field">
                <div
                  class="file-card"
                  :class="[
                    formData.autoGeneratedTaxpodInvoice.length > 0 ? 'file-card--has-file' : 'file-card--empty',
                    { 'file-card--struck': isSpecialCnCase }
                  ]"
                  @drop.prevent="addDropMultipleFile($event, 'autoGeneratedTaxpodInvoice', 'pdf.*')"
                  @dragover.prevent
                >
                  <div class="file-card__icon-wrap">
                    <div class="file-card__icon">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__svg">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                      </svg>
                    </div>
                  </div>
                  <div class="file-card__info">
                    <span class="file-card__name">{{ formData.autoGeneratedTaxpodInvoice.length > 0 ? formData.autoGeneratedTaxpodInvoice.map(f => f.name).join(', ') : 'No file selected' }}</span>
                    <span class="file-card__hint">{{ formData.autoGeneratedTaxpodInvoice.length > 0 ? formData.autoGeneratedTaxpodInvoice.length + ' file(s) selected' : 'PDF only' }}</span>
                  </div>
                  <div class="file-card__actions">
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <label class="file-card__btn" :class="{ 'file-card__btn--disabled': isSpecialCnCase }" v-on="on">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__btn-icon"><path stroke-linecap="round" stroke-linejoin="round" d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13" /></svg>
                          <input type="file" class="file-card__hidden" accept=".pdf" multiple :disabled="isSpecialCnCase" @change="handleMultiFileChange($event, 'autoGeneratedTaxpodInvoice', 'clientInvNo')" />
                        </label>
                      </template>
                      <span>Upload</span>
                    </v-tooltip>
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <button class="file-card__btn" @click="previewAttachment('autoGeneratedTaxpodInvoice')" v-on="on">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__btn-icon"><path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" /><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /></svg>
                        </button>
                      </template>
                      <span>Preview</span>
                    </v-tooltip>
                  </div>
                </div>
                <div v-if="errorField.autoGeneratedTaxpodInvoice" class="field-error">{{ errorField.autoGeneratedTaxpodInvoice }}</div>
              </div>
            </div>

            <!-- CN Description -->
            <div v-if="isRefundCases && !isCnIgnored">
              <div
                class="form-row"
                v-for="(item, index) in clientInvoiceCount"
                :key="index"
              >
                <label class="form-label">CN Description {{ index + 1 }}</label>
                <div class="form-field">
                  <v-textarea
                    :disabled="isSpecialCnCase"
                    rows="4"
                    dense
                    filled
                    auto-grow
                    v-model="formData.cnDescription[index]"
                    :error-messages="errorField.cnDescription"
                    :class="{ 'text-decoration-line-through': isSpecialCnCase }"
                  ></v-textarea>
                </div>
              </div>
            </div>

            <!-- Payment Received Date -->
            <div class="form-row">
              <label class="form-label">Payment Received Date</label>
              <div class="form-field">
                <v-menu
                  ref="menu"
                  v-model="menu"
                  :close-on-content-click="false"
                  :return-value.sync="date"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <div class="rv-datepicker__trigger" v-bind="attrs" v-on="on">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="rv-datepicker__icon">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
                      </svg>
                      <span v-if="formData.paymentReceivedDate" class="rv-datepicker__text">{{ formData.paymentReceivedDate }}</span>
                      <span v-else class="rv-datepicker__placeholder">Select date (YYYY-MM-DD)</span>
                    </div>
                  </template>
                  <v-date-picker
                    v-model="formData.paymentReceivedDate"
                    no-title
                    scrollable
                  >
                    <v-spacer></v-spacer>
                    <v-btn text color="primary" @click="menu = false">Cancel</v-btn>
                    <v-btn text color="primary" @click="$refs.menu.save(date)">OK</v-btn>
                  </v-date-picker>
                </v-menu>
              </div>
            </div>

            <!-- Generate CN Options (Toggle Cards) -->
            <div class="form-row" v-if="isRefundCases">
              <label class="form-label">Generate CN Options</label>
              <div class="form-field">
                <div class="toggle-card-group">
                  <div
                    class="toggle-card"
                    :class="{ 'toggle-card--active-blue': formData.byPassCnBool === 'true' }"
                    @click="formData.byPassCnBool = 'true'"
                  >
                    <div class="toggle-card__icon-wrap toggle-card__icon-wrap--blue">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="toggle-card__icon">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m3.75 9v6m3-3H9m1.5-12H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                      </svg>
                    </div>
                    <div class="toggle-card__content">
                      <span class="toggle-card__title">Generate CN</span>
                      <span class="toggle-card__desc">Auto generated</span>
                    </div>
                    <div class="toggle-card__check" :class="{ 'toggle-card__check--active': formData.byPassCnBool === 'true' }">
                      <svg v-if="formData.byPassCnBool === 'true'" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor" class="toggle-card__check-icon">
                        <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                      </svg>
                    </div>
                  </div>
                  <div
                    class="toggle-card"
                    :class="{ 'toggle-card--active-red': formData.byPassCnBool === 'doNotGenerate' }"
                    @click="formData.byPassCnBool = 'doNotGenerate'"
                  >
                    <div class="toggle-card__icon-wrap toggle-card__icon-wrap--red">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="toggle-card__icon">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M18.364 18.364A9 9 0 0 0 5.636 5.636m12.728 12.728A9 9 0 0 1 5.636 5.636m12.728 12.728L5.636 5.636" />
                      </svg>
                    </div>
                    <div class="toggle-card__content">
                      <span class="toggle-card__title">Do Not Generate</span>
                      <span class="toggle-card__desc">Upload manually</span>
                    </div>
                    <div class="toggle-card__check" :class="{ 'toggle-card__check--active toggle-card__check--red': formData.byPassCnBool === 'doNotGenerate' }">
                      <svg v-if="formData.byPassCnBool === 'doNotGenerate'" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor" class="toggle-card__check-icon">
                        <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                      </svg>
                    </div>
                  </div>
                  <div
                    v-if="editMode"
                    class="toggle-card"
                    :class="{ 'toggle-card--active-purple': formData.byPassCnBool === 'regenerate' }"
                    @click="formData.byPassCnBool = 'regenerate'"
                  >
                    <div class="toggle-card__icon-wrap toggle-card__icon-wrap--purple">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="toggle-card__icon">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182M2.985 19.644l3.181-3.183" />
                      </svg>
                    </div>
                    <div class="toggle-card__content">
                      <span class="toggle-card__title">Regenerate CN</span>
                      <span class="toggle-card__desc">Claim Basis changed</span>
                    </div>
                    <div class="toggle-card__check" :class="{ 'toggle-card__check--active toggle-card__check--purple': formData.byPassCnBool === 'regenerate' }">
                      <svg v-if="formData.byPassCnBool === 'regenerate'" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor" class="toggle-card__check-icon">
                        <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 30% Case -->
        <div class="section-card" v-if="formData.refundType == 2">
          <div class="section-header section-header--purple">
            <v-icon color="white" small>mdi-cash-multiple</v-icon>
            <span class="section-header__title">Payment Details</span>
            <span class="section-header__badge">30%</span>
          </div>
          <div class="section-body">
            <!-- Grant ID -->
            <div class="form-row">
              <label class="form-label">Grant ID</label>
              <div class="form-field">
                <div class="claim-copyable" @click="copyContent(formData.grantId)">
                  <span class="claim-copyable__text">{{ formData.grantId || '-' }}</span>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="claim-copyable__icon">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9.75a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184" />
                  </svg>
                </div>
              </div>
            </div>

            <!-- HRDC Invoice No. -->
            <div class="form-row">
              <label class="form-label">HRDC Invoice No.</label>
              <div class="form-field">
                <v-text-field
                  dense
                  filled
                  v-model="formData.invoiceNo2"
                  :error-messages="errorField.invoiceNo2"
                  disabled
                  :class="{ 'text-decoration-line-through': isSpecialCnCase }"
                ></v-text-field>
              </div>
            </div>

            <!-- Payment Received Date 2 -->
            <div class="form-row">
              <label class="form-label">Payment Received Date</label>
              <div class="form-field">
                <v-menu
                  ref="menu2"
                  v-model="menu2"
                  :close-on-content-click="false"
                  :return-value.sync="date"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <div class="rv-datepicker__trigger" v-bind="attrs" v-on="on">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="rv-datepicker__icon">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
                      </svg>
                      <span v-if="formData.paymentReceivedDate2" class="rv-datepicker__text">{{ formData.paymentReceivedDate2 }}</span>
                      <span v-else class="rv-datepicker__placeholder">Select date (YYYY-MM-DD)</span>
                    </div>
                  </template>
                  <v-date-picker
                    v-model="formData.paymentReceivedDate2"
                    no-title
                    scrollable
                  >
                    <v-spacer></v-spacer>
                    <v-btn text color="primary" @click="menu2 = false">Cancel</v-btn>
                    <v-btn text color="primary" @click="$refs.menu2.save(date)">OK</v-btn>
                  </v-date-picker>
                </v-menu>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <HrdcLoader :loadingDialog="loadingDialog"></HrdcLoader>
  </v-container>
</template>

<script>
import moment from "moment";
import { mapState } from "vuex";
import Api from "@/objects/api";
import Model from "@/objects/model";
import Service from "@/objects/service";
import hrdcFunction from "@/objects/hrdcFunction";
import HrdcData from "@/services/hrdc/objects/globalData";

export default {
  mixins: [hrdcFunction],
  computed: {
    ...mapState({
      auth: (state) => state.auth.data,
    }),
    isRefundCases() {
      if (this.formData.paymentChannel == 1) {
        return true;
      }
      return false;
    },
    clientInvoiceCount() {
      return this.formData.autoGeneratedTaxpodInvoice.length;
    },
    isSpecialCnCase() {
      return this.specialCnData.length > 0;
    },
    isCnIgnored() {
      return this.formData.byPassCnBool == "doNotGenerate";
    },
  },
  props: ["params"],
  data: () => ({
    apiGetApplicationSingleDetail: new Api(),
    apiGetClientInvoiceData: new Api(),
    apiGetXeroRefreshToken: new Api(),
    apiRefreshXeroToken: new Api(),
    apiScanPdfText: new Api(),
    apiSubmit: new Api(),
    model: new Model(),
    service: new Service(),
    hrdcData: new HrdcData(),
    alertError: [],
    date: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
      .toISOString()
      .substr(0, 10),
    menu: false,
    menu2: false,
    formData: {
      applicationNo: null,
      claimId: null,
      grantId: null,
      invoiceNo: null,
      invoiceNo2: null,
      paymentReceivedDate: new Date(
        Date.now() - new Date().getTimezoneOffset() * 60000
      )
        .toISOString()
        .substr(0, 10),
      paymentReceivedDate2: new Date(
        Date.now() - new Date().getTimezoneOffset() * 60000
      )
        .toISOString()
        .substr(0, 10),
      refundType: null,
      refundTypeDesc: null,
      paymentChannel: null,
      autoGeneratedTaxpodInvoice: [],
      byPassCnBool: "true",
      subscriptionStartDate: null,
      subscriptionEndDate: null,
      cnDescription: [],
      clientInvNo: null,
      creditNote: [],
      creditNoteNo: null,
    },
    errorField: {
      applicationNo: null,
      claimId: null,
      grantId: null,
      invoiceNo: null,
      invoiceNo2: null,
      paymentReceivedDate: null,
      paymentReceivedDate2: null,
      autoGeneratedTaxpodInvoice: null,
      subscriptionStartDate: null,
      subscriptionEndDate: null,
      cnDescription: [],
      creditNote: null,
    },
    loadingDialog: false,
    paymentReceivedSubmitDialog: false,
    editMode: false,
    btnDisabled: false,
    menuSubscriptionStartDate: false,
    menuSubscriptionEndDate: false,
    paymentReceivedError: [],
    specialCnData: [],
  }),
  async created() {
    this.showLoadingDialog();
    this.formData.applicationNo = this.$route.query._ausk;
    this.model.getByKey(this.params.modelKey);
    if (this.model.serviceKey) this.service.set("key", this.model.serviceKey);

    // Assign apiDetails by mapping repository.
    const apiDetails = {
      apiSubmit: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/payment_received/create-payment-received`,
        method: "post",
      },
      apiGetClientInvoiceData: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/payment_received/get-client-invoice-data`,
        method: "post",
      },
      apiGetApplicationSingleDetail: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/application/get-single-application`,
        method: "post",
        params: {
          applicationUuid: this.$route.query._ausk,
        },
      },
      apiGetXeroRefreshToken: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/xero/get-xero-token?date=${moment().format(
          "YYYY-MM-DD"
        )}`,
      },
      apiRefreshXeroToken: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/xero/update-xero-token`,
        method: "put",
      },
      apiScanPdfText: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/payment_received/scan-pdf`,
        method: "post",
      },
    };

    for (const api in apiDetails) {
      if (apiDetails[api].url !== undefined) {
        this[api].setUrl(apiDetails[api].url);
      }
      if (apiDetails[api].method !== undefined) {
        this[api].setMethod(apiDetails[api].method);
      }
      if (apiDetails[api].params !== undefined) {
        this[api].setParams(apiDetails[api].params);
      }
    }

    this.stages = await this.hrdcData.allStages;
    const currentStageId =
      this.hrdcData.stageIndex[this.$store.state.hrdc.data.stageId];
    this.rolesAssignation(this.auth.roles);
    if (!this.isTaxpodAdminRoles) {
      this.alertError.push("You are not allowed to perform this action!");
      this.btnDisabled = true;
    }

    this.checkFormAlreadyFilledGte(
      currentStageId,
      this.hrdcData.stageSlug.paymentReceivedFromHrdc
    );

    this.checkFormNotYetFilledLesser(
      currentStageId,
      this.hrdcData.stageSlug.claimApprovedFromHrdc
    );

    this.apiGetApplicationSingleDetail.setCallbackCompleted((response) => {
      try {
        const { status, data } = response;
        if (status) {
          const paymentReceivedStageId =
            this.hrdcData.stagePriority.paymentReceivedFromHrdc;

          const paymentReceived2StageId =
            this.hrdcData.stagePriority.paymentReceivedFromHrdc2;

          this.formData.refundType = data.refundType;
          if (this.formData.refundType == 1) {
            this.formData.refundTypeDesc = "Normal";
          } else {
            this.formData.refundTypeDesc = "70:30";
          }

          // Append payment channel.
          this.formData.paymentChannel = data.paymentChannel;

          // Append auto generated taxpod invoice if any.
          const autoGeneratedTaxpodInvoiceObj =
            data.ApplicationAttachment.filter(
              (item) =>
                item.stageId ==
                this.hrdcData.stagePriority.autoGeneratedTaxpodInvoice
            );

          if (autoGeneratedTaxpodInvoiceObj.length > 0) {
            let fileCounter = 0;
            const formData = new FormData();
            for (const taxpodInvoiceObj of autoGeneratedTaxpodInvoiceObj) {
              const parts = taxpodInvoiceObj.fileUrl.split(".");
              const extension = parts[parts.length - 1];

              // Create a Blob object from the data
              const blob = new Blob([
                new Uint8Array(taxpodInvoiceObj.dataArray),
              ]);

              this.formData.autoGeneratedTaxpodInvoice.push(
                new File([blob], taxpodInvoiceObj.fileName, {
                  type: `application/${extension}`,
                })
              );

              // Get the description from client invoice.
              formData.append(
                `autoGeneratedTaxpodInvoice_${fileCounter}`,
                this.formData.autoGeneratedTaxpodInvoice[fileCounter]
              );
              fileCounter++;
            }

            formData.append("fileCounter", fileCounter);

            this.apiGetClientInvoiceData.setParams(formData);
            this.apiGetClientInvoiceData.setCallbackCompleted((response) => {
              try {
                const { status, data } = response;
                if (status) {
                  this.formData.cnDescription = data;
                }
              } catch (err) {
                console.log(err);
              }
            });
            this.apiGetClientInvoiceData.fetch();
          }

          // Append company taxpod subscription period.
          let subscriptionPeriod = null;
          if (data.CompanyTaxpodSubscription.length > 0) {
            subscriptionPeriod = data.CompanyTaxpodSubscription[0];
            this.formData.subscriptionStartDate =
              subscriptionPeriod.subscriptionStartDate;
            this.formData.subscriptionEndDate =
              subscriptionPeriod.subscriptionEndDate;
          }

          if (this.editMode) {
            let paymentReceivedDate, paymentReceivedDate2;

            let paymentReceived = data.ApplicationLog.find(
              (item) => item.stageId == paymentReceivedStageId
            );
            if (paymentReceived) {
              paymentReceivedDate = paymentReceived.startDate;

              this.formData.paymentReceivedDate =
                moment(paymentReceivedDate).format("YYYY-MM-DD");
            }

            let paymentReceived2 = data.ApplicationLog.find(
              (item) => item.stageId == paymentReceived2StageId
            );
            if (paymentReceived2) {
              paymentReceivedDate2 = paymentReceived2.startDate;

              this.formData.paymentReceivedDate2 =
                moment(paymentReceivedDate2).format("YYYY-MM-DD");
            }
          }

          this.formData.claimId = data.claimId;
          this.formData.grantId = data.grantApprovalNo;
          this.formData.invoiceNo = data.manualInvoiceToHrdc;
          this.formData.invoiceNo2 = data.manualInvoiceToHrdc2;
          this.formData.clientInvNo = data.autoGeneratedTaxpodInvoiceNo;

          // Append special cn case data.
          if (data.ApplicationSpecialCn.length > 0) {
            this.specialCnData = data.ApplicationSpecialCn;
          }
        }

        this.hideLoadingDialog();
      } catch (err) {
        console.log(err);
      }
    });
    this.apiGetApplicationSingleDetail.fetch();
  },
  methods: {
    clearErrorMsg(field = null) {
      if (!field) {
        for (let error in this.errorField) {
          this.errorField[error] = null;
        }
      } else {
        this.errorField[field] = null;
      }
    },
    showErrorMessage(response) {
      for (const key in response.data) {
        this.errorField[key] = null;
        this.errorField[key] = response.data[key];
      }
    },
    modalPaymentReceivedSubmit() {
      // Check with XERO if pay & refund, to ensure client invoice is Paid.
      if (
        this.isRefundCases &&
        !this.editMode &&
        this.getEnv() == "production"
      ) {
        this.checkClientInvFromXeroAndSubmit();
      } else {
        this.paymentReceivedSubmitDialog = false;
        this.submit();
      }
    },
    redirectBack() {
      let route = "HrdcApplication";
      if (this.params.backTo) {
        route = this.params.backTo;
      }

      this.$store.commit("assignDeveloperData", {
        paginationPageNo: this.$route.query.currentPageNo,
      });

      this.$router.push({
        name: route,
        params: {
          modelKey: this.model.key,
          backTo: "",
        },
        query: {
          viewId: this.$store.state.hrdc.data.viewId,
          _ausk: this.$route.query._ausk,
        },
      });
    },
    handleMultiFileChange($event, fileType, displayFileInvoiceNo) {
      const files = Array.from($event.target.files);
      if (files.length === 0) return;
      this.formData[fileType] = files;
      this.scanPdf(files, fileType, displayFileInvoiceNo);
    },
    scanPdf($event, fileType, displayFileInvoiceNo) {
      this.clearErrorMsg(fileType);
      this.clearErrorMsg(displayFileInvoiceNo);
      if ($event.length > 0) {
        this.showLoadingDialog();

        const fd = new FormData();

        let counter = 0;
        for (const file of this.formData[fileType]) {
          fd.append(`${fileType}_${counter}`, file);
          counter++;
        }

        fd.append("fileType", fileType);
        fd.append("counter", counter);

        this.apiScanPdfText.setParams(fd);
        this.apiScanPdfText.setCallbackCompleted((response) => {
          try {
            const { status, data } = response;

            if (status) {
              const invNo = data.map((item) => item.invoiceNo).join(",");

              if (invNo) {
                this.formData[displayFileInvoiceNo] = invNo;
              } else {
                this.$store.dispatch("showMessage", {
                  message: "Please upload a valid invoice",
                  messageType: "error",
                  timeout: 2000,
                });
              }
            } else {
              this.formData[displayFileInvoiceNo] = null;
              this.$store.dispatch("showMessage", {
                message: "Unable to scan text!",
                messageType: "error",
                timeout: 2000,
              });
            }
            this.hideLoadingDialog();
          } catch (err) {
            console.log(err);
          }
        });
        this.apiScanPdfText.fetch();
      } else {
        // Empty the invoice no. if remove attachment.
        this.formData[displayFileInvoiceNo] = null;
      }
    },
    addDropFile(e, key, matchExtType) {
      let file = e.dataTransfer.files[0];

      if (matchExtType) {
        if (!file.type.match(matchExtType)) {
          switch (matchExtType) {
            case "pdf.*":
              this.errorField[key] = "Please select pdf file format";
              break;
            case "image/*":
              this.errorField[key] = "Please select only image";
              break;
          }
          return;
        }
      }

      this.formData[key] = file;

      if (e) {
        this.clearErrorMsg(key);
      }
    },
    addDropMultipleFile(e, key, extType) {
      for (const file of e.dataTransfer.files) {
        if (!file.type.match(extType)) {
          this.errorField[key] = "Please select correct file format";
          return;
        }

        this.formData[key].push(file);
      }
    },
    copyContent(item) {
      const textarea = document.createElement("textarea");
      textarea.value = item;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");

      // Remove the temporary textarea
      document.body.removeChild(textarea);

      this.$store.dispatch("showMessage", {
        message: "Copied Successfully!",
        messageType: "info",
      });
    },
    previewAttachment(fileType) {
      if (this.formData[fileType].length == 0) {
        this.$store.dispatch("showMessage", {
          message: "No Preview Available",
          messageType: "info",
        });
        return;
      }

      let url = null;
      let newWindow = null;
      for (const file of this.formData[fileType]) {
        switch (fileType) {
          default:
            url = URL.createObjectURL(file);
            newWindow = window.open(url, "_blank");
            break;
        }

        if (newWindow) {
          newWindow.onload = function () {
            // Revoke the object URL when the window is closed
            newWindow.addEventListener("beforeunload", function () {
              URL.revokeObjectURL(url);
            });
          };
        }
      }
    },
    async checkClientInvFromXeroAndSubmit() {
      this.showLoadingDialog();

      this.paymentReceivedError = [];

      let clientInvNo = this.formData.clientInvNo;

      // If client invoice is empty, retrieve client inv no. from pdf.
      if (!clientInvNo || clientInvNo == "") {
        if (this.clientInvoiceCount == 0) {
          this.paymentReceivedError.push(
            "Please upload Client Invoice to proceed!"
          );

          this.hideLoadingDialog();
          return false;
        }
      }
      const clientInvNoArr = this.formData.clientInvNo.split(",");
      const clientInvNoCount = clientInvNoArr.length;

      this.apiGetXeroRefreshToken.setCallbackCompleted(async (xeroTokenRes) => {
        try {
          if (!xeroTokenRes || !xeroTokenRes.status) {
            this.paymentReceivedError.push(
              "Failed to check Client Invoice with XERO, please contact admin for support!"
            );
            return false;
          }

          if (xeroTokenRes.data) {
            const refreshToken = xeroTokenRes.data.token;

            const token = await this.xeroRequestAccessToken(
              `${
                this.$service[this.service.key]
              }/v1/en/console/xero/request-xero-access-token`,
              this.$store.state.auth.data.token,
              refreshToken
            );

            const accessToken = token.data.access_token;

            // Refresh the new refresh token in db.
            this.apiRefreshXeroToken.setParams({
              refresh_token: token.data.refresh_token,
              token_id: xeroTokenRes.data.id,
            });
            this.apiRefreshXeroToken.fetch();

            const connectionResponse = await this.xeroRequestTenantId({
              url: `${
                this.$service[this.service.key]
              }/v1/en/console/xero/get-xero-tenant-id`,
              headers: {
                Authorization: `Bearer ${this.$store.state.auth.data.token}`,
              },
              postData: {
                accessToken,
              },
            });

            if (!connectionResponse || !connectionResponse.status) {
              throw new Error(
                "Unable to generate HRDC Invoice due to XERO invalid response, please contact administrator for assistance (2)"
              );
            }

            const connectionData = connectionResponse.data;
            const connection = connectionData[connectionData.length - 1];
            const tenantId = connection.tenantId;

            const InvoiceResponse = await this.xeroCallApi({
              authorization: this.$store.state.auth.data.token,
              service_key: this.model.serviceKey,
              access_token: accessToken,
              xero_tenant_id: tenantId,
              api_type: "get_invoices",
              method: "get",
              postData: null,
              url_suffix: `?InvoiceNumbers=${clientInvNo}&Statuses=Paid&summaryOnly=true`,
            });

            const InvoiceData = InvoiceResponse.data.Invoices;
            const InvoiceNoArr = InvoiceData.map((item) => item.InvoiceNumber);
            const InvoiceDataCount = InvoiceData.length;
            if (InvoiceDataCount == 0) {
              this.paymentReceivedError.push(
                `${clientInvNo} client invoice is haven't paid in XERO, please update invoice to PAID before proceed generate CN!`
              );
              return false;
            }

            if (InvoiceDataCount !== clientInvNoCount) {
              const missingElements = clientInvNoArr.filter(
                (item) => !InvoiceNoArr.includes(item)
              );
              this.paymentReceivedError.push(
                `${missingElements.join(
                  ","
                )} client invoice is haven't paid in XERO, please update invoice to PAID before proceed generate CN!`
              );
              return false;
            }

            this.submit();
          }
        } catch (err) {
          console.log(err);
          this.paymentReceivedError.push(
            "Failed to check Client Invoice with XERO, please contact admin for support!"
          );
        } finally {
          this.hideLoadingDialog();
        }
      });

      this.apiGetXeroRefreshToken.fetch();
    },
    async submit() {
      this.showLoadingDialog();
      const fd = new FormData();
      fd.append("applicationUuid", this.formData.applicationNo);
      fd.append("paymentReceivedDate", this.formData.paymentReceivedDate);
      fd.append("paymentReceivedDate2", this.formData.paymentReceivedDate2);
      fd.append("refundType", this.formData.refundType);
      fd.append("referById", this.auth.uuid);
      fd.append("editMode", this.editMode);
      fd.append("paymentChannel", this.formData.paymentChannel);
      fd.append("byPassCnBool", this.formData.byPassCnBool);
      fd.append("subscriptionStartDate", this.formData.subscriptionStartDate);
      fd.append("subscriptionEndDate", this.formData.subscriptionEndDate);
      fd.append("specialCnData", JSON.stringify(this.specialCnData));

      let cnFileCounter = 0;
      if (this.isCnIgnored) {
        fd.append("creditNoteNo", this.formData.creditNoteNo);

        for (const file of this.formData.creditNote) {
          fd.append(`creditNote_${cnFileCounter}`, file);
          cnFileCounter++;
        }
      }

      fd.append("cnFileCounter", cnFileCounter);

      let fileCounter = 0;
      if (this.formData.autoGeneratedTaxpodInvoice) {
        for (const file of this.formData.autoGeneratedTaxpodInvoice) {
          fd.append(`autoGeneratedTaxpodInvoice_${fileCounter}`, file);
          fd.append(
            `cnDescription_${fileCounter}`,
            this.formData.cnDescription[fileCounter]
          );
          fileCounter++;
        }
      }

      fd.append("fileCounter", fileCounter);

      this.apiSubmit.setParams(fd);
      this.apiSubmit.setCallbackCompleted((response) => {
        try {
          this.clearErrorMsg();
          this.hideLoadingDialog();
          this.paymentReceivedSubmitDialog = false;

          if (!response.status) {
            this.showErrorMessage(response);
            this.$store.dispatch("showMessage", {
              message:
                response.message ?? "Please fill-in all the required fields",
              messageType: "error",
              timeout: 2000,
            });
          }

          if (response.status) {
            this.$store.commit("assignDeveloperData", {
              paginationPageNo: this.$route.query.currentPageNo,
            });

            this.$router.push({
              name: "HrdcApplication",
              query: {
                viewId: this.$store.state.hrdc.data.viewId,
              },
            });
            this.$store.dispatch("showMessage", {
              message: response.data,
              messageType: "success",
              timeout: 2000,
            });
          }
        } catch (err) {
          console.log(err);
        }
      });
      this.apiSubmit.fetch();
    },
  },
};
</script>

<style scoped>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .claim-page {
  background-color: var(--app-page);
}

.dark-mode .claim-header {
  background: var(--app-elevated);
}

.dark-mode .back-btn {
  background: var(--app-form-tile);
  border: 1px solid var(--app-border-strong);
}

.dark-mode .back-btn:hover {
  background: var(--app-form-tile-hover);
  border-color: var(--app-border-strong);
}

.dark-mode .hero-action-btn {
  background: var(--app-form-tile);
  color: var(--app-text-muted);
}

.dark-mode .hero-action-btn:hover {
  background: var(--app-form-tile-hover);
  color: var(--app-text);
}

.dark-mode .hero-action-btn--green {
  background: var(--app-form-tile);
  color: var(--app-text);
}

.dark-mode .hero-action-btn--green:hover {
  background: var(--app-form-tile-hover);
  color: var(--app-text);
}

.dark-mode .section-card {
  background: var(--app-elevated);
}

.dark-mode .section-body {
  background: var(--app-card);
}

.dark-mode .claim-copyable {
  background: var(--app-form-tile);
  border: 1px dashed var(--app-border);
}

.dark-mode .claim-copyable:hover {
  background: var(--app-accent-soft);
}

.dark-mode .claim-copyable__text {
  color: var(--app-text);
}

.dark-mode .claim-copyable__icon {
  color: var(--app-text-faint);
}

.dark-mode .date-range__separator {
  color: var(--app-text-faint);
}

.dark-mode .rv-datepicker__trigger {
  border: 1px solid var(--app-field-border);
  background: var(--app-field);
}

.dark-mode .rv-datepicker__trigger:hover {
  border-color: var(--app-field-border-hover);
}

.dark-mode .rv-datepicker__icon {
  color: var(--app-text-faint);
}

.dark-mode .rv-datepicker__text {
  color: var(--app-text);
}

.dark-mode .rv-datepicker__placeholder {
  color: var(--app-text-faint);
}

.dark-mode .file-card {
  background: var(--app-surface);
}

.dark-mode .file-card:hover {
  background: var(--app-form-tile-hover);
}

.dark-mode .file-card__icon {
  background: var(--app-danger-bg);
}

.dark-mode .file-card__icon-wrap::after {
  border: 2px solid var(--app-border);
}

.dark-mode .file-card--has-file .file-card__icon-wrap::after {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke-width='3' stroke='var(--app-card)'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='m4.5 12.75 6 6 9-13.5'/%3E%3C/svg%3E");
}

.dark-mode .file-card--empty .file-card__icon-wrap::after {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke-width='3' stroke='var(--app-card)'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M6 18 18 6M6 6l12 12'/%3E%3C/svg%3E");
}

.dark-mode .file-card__name {
  color: var(--app-text);
}

.dark-mode .file-card__hint {
  color: var(--app-text-faint);
}

.dark-mode .file-card__btn {
  background: var(--app-form-tile);
  color: var(--app-text-muted);
}

.dark-mode .file-card__btn:hover {
  background: var(--app-form-tile-hover);
  color: var(--app-text);
}

.dark-mode .toggle-card {
  border: 1.5px solid var(--app-border);
  background: var(--app-form-tile);
}

.dark-mode .toggle-card:hover {
  border-color: var(--app-border-strong);
}

.dark-mode .toggle-card--active-blue {
  background: var(--app-accent-soft);
}

.dark-mode .toggle-card--active-red {
  background: var(--app-danger-bg);
}

.dark-mode .toggle-card--active-purple {
  background: var(--app-purple-bg);
}

.dark-mode .toggle-card__icon-wrap--blue {
  background: var(--app-accent-soft);
}

.dark-mode .toggle-card__icon-wrap--red {
  background: var(--app-danger-bg);
}

.dark-mode .toggle-card__icon-wrap--purple {
  background: var(--app-purple-bg);
}

.dark-mode .toggle-card__title {
  color: var(--app-text);
}

.dark-mode .toggle-card__desc {
  color: var(--app-text-faint);
}

.dark-mode .toggle-card__check {
  border: 2px solid var(--app-border-strong);
}

.dark-mode .cn-description {
  color: var(--app-text-muted);
}

.dark-mode .cn-card {
  background: var(--app-surface);
  border: 1px solid var(--app-border);
}

.dark-mode .cn-card:hover {
  border-color: var(--app-border-strong);
}

.dark-mode .cn-detail-label {
  color: var(--app-text-muted);
}

.dark-mode .cn-detail-value {
  color: var(--app-text);
}

.dark-mode .claim-confirm-card {
  background: var(--app-form-tile);
}

.dark-mode .claim-confirm-card__title {
  color: var(--app-text);
}

.dark-mode .claim-confirm-card__message {
  color: var(--app-text-muted);
}

.dark-mode .claim-confirm-card__btn--cancel {
  background: var(--app-form-tile);
  border: 1.5px solid var(--app-border-strong);
}

.dark-mode .claim-confirm-card__btn--cancel:hover {
  background: var(--app-form-tile-hover);
}

.dark-mode .form-field >>> .v-text-field--filled:not(.v-input--is-disabled) > .v-input__control > .v-input__slot {
  background: var(--app-field) !important;
  border: 1.5px solid var(--app-field-border) !important;
}

.dark-mode .form-field >>> .v-text-field--filled:not(.v-input--is-disabled) > .v-input__control > .v-input__slot:hover {
  border-color: var(--app-field-border-hover) !important;
}

.dark-mode .form-field >>> .v-input--is-disabled .v-input__slot {
  background: var(--app-field-disabled) !important;
  border: 1px dashed var(--app-field-disabled-border) !important;
}

.dark-mode .form-field >>> .v-input--is-disabled input,
.dark-mode .form-field >>> .v-input--is-disabled textarea,
.dark-mode .form-field >>> .v-input--is-disabled .v-select__selection {
  color: var(--app-text-faint) !important;
}

.dark-mode .form-field >>> .v-label {
  color: var(--app-text-muted);
}

/* >>> end auto-dark <<< */

/* ============================================
   PAGE WRAPPER
   ============================================ */
.claim-page {
  background-color: #d8dce2;
  /* Fills main minus the breadcrumb and footer; a `100vh - 64px` guess only
     subtracts the app bar and overflows. See .layout-main__content. */
  flex: 1 0 auto;
  padding-top: 24px !important;
  padding-bottom: 60px !important;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
  zoom: 1.1;
}

/* ============================================
   HEADER
   ============================================ */
.claim-header {
  background: #eaecef;
  border-radius: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
  margin-bottom: 24px;
  overflow: hidden;
}

.claim-header__top {
  display: flex;
  align-items: center;
  padding: 24px 28px 16px;
}

.back-btn {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: 1px solid #d1d5db;
  cursor: pointer;
  margin-right: 16px;
  transition: all 0.2s ease;
}

.back-btn:hover {
  background: #f8fafc;
  border-color: #94a3b8;
}

.claim-header__info {
  flex: 1;
  min-width: 0;
}

.claim-header__title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #3a3f47;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  letter-spacing: -0.2px;
}

.claim-header__actions {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-left: 16px;
}

/* ============================================
   HERO ACTION BUTTONS
   ============================================ */
.hero-action-btn {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: none;
  background: #f1f5f9;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
}

.hero-action-btn:hover {
  background: #e2e8f0;
  color: #334155;
  transform: translateY(-1px);
}

.hero-action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.hero-icon {
  width: 18px;
  height: 18px;
}

.hero-action-btn--green {
  background: #dcfce7;
  color: #16a34a;
}

.hero-action-btn--green:hover {
  background: #d1fae5;
  color: #059669;
}

/* ============================================
   SECTION CARDS
   ============================================ */
.claim-sections {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.content-grid {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.content-grid--split {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  align-items: start;
}

.section-card {
  background: #eaecef;
  border-radius: 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
  overflow: hidden;
  transition: box-shadow 0.3s ease;
}

.section-card:hover {
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.06);
}

.section-header {
  background: linear-gradient(135deg, #0ea5e9, #38bdf8);
  padding: 13px 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.section-header--amber {
  background: linear-gradient(135deg, #f59e0b, #fbbf24);
}

.section-header--purple {
  background: linear-gradient(135deg, #8b5cf6, #a78bfa);
}

.section-header__title {
  color: #ffffff;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 2px;
  text-transform: uppercase;
  flex: 1;
}

.section-body {
  padding: 22px;
  background: #fff;
}

/* ============================================
   SECTION HEADER BADGE (70%/30%)
   ============================================ */
.section-header__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 10px;
  border-radius: 6px;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.5px;
  background: rgba(255, 255, 255, 0.25);
  color: #fff;
  margin-left: auto;
}

/* ============================================
   FORM ROWS & FIELDS
   ============================================ */
.form-row {
  display: flex;
  align-items: flex-start;
  margin-bottom: 12px;
  gap: 12px;
}

.form-label {
  flex: 0 0 180px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #2d3239;
  line-height: 1.6;
  padding-top: 10px;
  display: flex;
  align-items: center;
}

.form-field {
  flex: 1;
  min-width: 0;
}

.field-error {
  font-size: 0.75rem;
  color: #ef4444;
  margin-top: 4px;
  font-family: 'Lato', sans-serif;
}

/* ============================================
   COPY BUTTON
   ============================================ */
.claim-copyable {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 42px;
  padding: 8px 14px;
  border-radius: 10px;
  background: #f1f5f9;
  border: 1px dashed #e2e8f0;
  cursor: pointer;
  transition: all 0.15s ease;
}

.claim-copyable:hover {
  background: #e0f2fe;
  border-color: #0ea5e9;
}

.claim-copyable:active {
  background: #bae6fd;
}

.claim-copyable__text {
  font-size: 0.85rem;
  font-weight: 600;
  color: #334155;
  font-family: 'Lato', sans-serif;
}

.claim-copyable__icon {
  width: 16px;
  height: 16px;
  color: #94a3b8;
  flex-shrink: 0;
  transition: color 0.15s;
}

.claim-copyable:hover .claim-copyable__icon {
  color: #0ea5e9;
}

/* ============================================
   DATE RANGE
   ============================================ */
.date-range {
  display: flex;
  align-items: center;
  gap: 10px;
}

.date-range__separator {
  font-size: 0.82rem;
  font-weight: 600;
  color: #94a3b8;
  flex-shrink: 0;
}

/* ============================================
   DATEPICKER TRIGGER
   ============================================ */
.rv-datepicker__trigger {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 42px;
  padding: 6px 12px;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: 'Lato', sans-serif;
  font-size: 0.85rem;
  flex: 1;
}

.rv-datepicker__trigger:hover {
  border-color: #94a3b8;
}

.rv-datepicker__icon {
  width: 18px;
  height: 18px;
  color: #94a3b8;
  flex-shrink: 0;
}

.rv-datepicker__text {
  color: #1e293b;
  font-weight: 600;
}

.rv-datepicker__placeholder {
  color: #94a3b8;
}

/* ============================================
   FILE CARD (attachment UI)
   ============================================ */
.file-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border-radius: 12px;
  background: #f8fafc;
  transition: background 0.15s;
}

.file-card:hover {
  background: #f1f5f9;
}

.file-card__icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #fef2f2;
  color: #ef4444;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.file-card__icon-wrap {
  position: relative;
  flex-shrink: 0;
}

.file-card__icon-wrap::after {
  content: '';
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid #f8fafc;
  background-size: 10px;
  background-position: center;
  background-repeat: no-repeat;
}

.file-card--has-file .file-card__icon-wrap::after {
  background-color: #22c55e;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke-width='3' stroke='white'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='m4.5 12.75 6 6 9-13.5'/%3E%3C/svg%3E");
}

.file-card--empty .file-card__icon-wrap::after {
  background-color: #ef4444;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke-width='3' stroke='white'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M6 18 18 6M6 6l12 12'/%3E%3C/svg%3E");
}

.file-card--struck {
  opacity: 0.5;
  text-decoration: line-through;
}

.file-card__svg {
  width: 20px;
  height: 20px;
}

.file-card__info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.file-card__name {
  font-size: 0.82rem;
  font-weight: 500;
  color: #334155;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-card__hint {
  font-size: 0.7rem;
  color: #94a3b8;
}

.file-card__actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.file-card__btn {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: none;
  background: #f1f5f9;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}

.file-card__btn:hover {
  background: #e2e8f0;
  color: #334155;
}

.file-card__btn--disabled {
  opacity: 0.4;
  pointer-events: none;
  cursor: not-allowed;
}

.file-card__btn-icon {
  width: 16px;
  height: 16px;
}

.file-card__hidden {
  display: none;
}

/* ============================================
   TOGGLE CARDS (radio replacement)
   ============================================ */
.toggle-card-group {
  display: flex;
  gap: 10px;
}

.toggle-card {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1.5px solid #e2e8f0;
  background: #fff;
  cursor: pointer;
  transition: all 0.18s ease;
  position: relative;
}

.toggle-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transform: translateY(-1px);
}

.toggle-card--active-blue {
  border-color: #0ea5e9;
  background: #f0f9ff;
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
}

.toggle-card--active-blue:hover {
  border-color: #0ea5e9;
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.15);
}

.toggle-card--active-red {
  border-color: #f87171;
  background: #fef2f2;
  box-shadow: 0 0 0 3px rgba(248, 113, 113, 0.1);
}

.toggle-card--active-red:hover {
  border-color: #f87171;
  box-shadow: 0 0 0 3px rgba(248, 113, 113, 0.15);
}

.toggle-card--active-purple {
  border-color: #a78bfa;
  background: #f5f3ff;
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
}

.toggle-card--active-purple:hover {
  border-color: #a78bfa;
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.15);
}

.toggle-card__icon-wrap {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.toggle-card__icon-wrap--blue {
  background: #e0f2fe;
  color: #0ea5e9;
}

.toggle-card--active-blue .toggle-card__icon-wrap--blue {
  background: #0ea5e9;
  color: #fff;
}

.toggle-card__icon-wrap--red {
  background: #fee2e2;
  color: #ef4444;
}

.toggle-card--active-red .toggle-card__icon-wrap--red {
  background: #ef4444;
  color: #fff;
}

.toggle-card__icon-wrap--purple {
  background: #ede9fe;
  color: #8b5cf6;
}

.toggle-card--active-purple .toggle-card__icon-wrap--purple {
  background: #8b5cf6;
  color: #fff;
}

.toggle-card__icon {
  width: 18px;
  height: 18px;
}

.toggle-card__content {
  flex: 1;
  min-width: 0;
}

.toggle-card__title {
  display: block;
  font-size: 0.82rem;
  font-weight: 700;
  color: #1e293b;
  font-family: 'Lato', sans-serif;
  line-height: 1.3;
}

.toggle-card__desc {
  display: block;
  font-size: 0.72rem;
  font-weight: 400;
  color: #94a3b8;
  font-family: 'Lato', sans-serif;
  line-height: 1.4;
  margin-top: 1px;
}

.toggle-card__check {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid #d1d5db;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.15s ease;
}

.toggle-card__check--active {
  background: #0ea5e9;
  border-color: #0ea5e9;
}

.toggle-card__check--red {
  background: #ef4444;
  border-color: #ef4444;
}

.toggle-card__check--purple {
  background: #8b5cf6;
  border-color: #8b5cf6;
}

.toggle-card__check-icon {
  width: 13px;
  height: 13px;
  color: #fff;
}

/* ============================================
   CREDIT NOTE CARDS
   ============================================ */
.cn-description {
  font-size: 0.85rem;
  color: #64748b;
  margin: 0 0 16px;
  font-weight: 500;
}

.cn-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 12px;
}

.cn-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  transition: all 0.15s ease;
}

.cn-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.cn-card__badge {
  display: inline-block;
  background: linear-gradient(135deg, #f59e0b, #fbbf24);
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  padding: 3px 10px;
  border-radius: 6px;
  margin-bottom: 12px;
}

.cn-card__details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.cn-detail-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.cn-detail-label {
  flex: 0 0 120px;
  font-size: 0.78rem;
  font-weight: 600;
  color: #64748b;
}

.cn-detail-value {
  flex: 1;
  font-size: 0.85rem;
  font-weight: 500;
  color: #1e293b;
  word-break: break-word;
}

.cn-detail-value--amount {
  font-weight: 700;
  color: #059669;
}

/* ============================================
   CONFIRM DIALOG
   ============================================ */
.claim-confirm-dialog {
  border-radius: 20px !important;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12) !important;
}

.claim-confirm-card {
  background: #fff;
  border-radius: 20px;
  padding: 32px 32px 24px;
  text-align: center;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
}

.claim-confirm-card__title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 10px;
}

.claim-confirm-card__message {
  font-size: 0.9rem;
  color: #64748b;
  margin: 0 0 24px;
  line-height: 1.5;
}

.claim-confirm-card__actions {
  display: flex;
  gap: 12px;
}

.claim-confirm-card__btn {
  flex: 1;
  padding: 12px 16px;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  font-family: 'Lato', sans-serif;
  cursor: pointer;
  transition: all 0.15s ease;
}

.claim-confirm-card__btn--cancel {
  background: #fff;
  color: #0ea5e9;
  border: 1.5px solid #d1d5db;
}

.claim-confirm-card__btn--cancel:hover {
  background: #f8fafc;
  border-color: #0ea5e9;
}

.claim-confirm-card__btn--confirm {
  background: #56bde8;
  color: #fff;
  border: none;
}

.claim-confirm-card__btn--confirm:hover {
  background: #3dadd8;
  box-shadow: 0 4px 12px rgba(86, 189, 232, 0.3);
}

/* ============================================
   VUETIFY INPUT OVERRIDES
   ============================================ */
.form-field >>> .v-input {
  margin-top: 0 !important;
  padding-top: 0 !important;
}

.form-field >>> .v-input__slot {
  border-radius: 10px !important;
  min-height: 40px !important;
}

.form-field >>> .v-text-field__details {
  margin-bottom: 0 !important;
  padding-top: 2px !important;
  min-height: 0 !important;
}

.form-field >>> .v-messages {
  min-height: 0 !important;
}

.form-field >>> .v-text-field--filled > .v-input__control > .v-input__slot {
  border-radius: 10px !important;
}

.form-field >>> .v-input--is-focused .v-input__slot {
  border-color: #56bde8 !important;
  box-shadow: 0 0 0 3px rgba(86, 189, 232, 0.12) !important;
}

.form-field >>> .v-input__slot::before,
.form-field >>> .v-input__slot::after {
  display: none !important;
}

/* Editable fields */
.form-field >>> .v-text-field--filled:not(.v-input--is-disabled) > .v-input__control > .v-input__slot {
  background: #fff !important;
  border: 1.5px solid #d1d5db !important;
}

.form-field >>> .v-text-field--filled:not(.v-input--is-disabled) > .v-input__control > .v-input__slot:hover {
  border-color: #94a3b8 !important;
}

/* Disabled fields */
.form-field >>> .v-input--is-disabled .v-input__slot {
  background: #f1f5f9 !important;
  border: 1px dashed #e2e8f0 !important;
  cursor: not-allowed !important;
}

.form-field >>> .v-input--is-disabled input,
.form-field >>> .v-input--is-disabled textarea,
.form-field >>> .v-input--is-disabled .v-select__selection {
  color: #94a3b8 !important;
}

.form-field >>> .v-text-field--filled:not(.v-input--is-focused) > .v-input__control > .v-input__slot::before {
  border: none !important;
}

.form-field >>> .v-text-field--filled > .v-input__control > .v-input__slot::after {
  border: none !important;
}

.form-field >>> .v-label {
  font-family: 'Lato', sans-serif;
  font-size: 0.85rem;
  color: #64748b;
}

.form-field >>> .v-text-field input,
.form-field >>> .v-select__selection,
.form-field >>> .v-text-field textarea {
  font-family: 'Lato', sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  color: #1a1e24;
}

/* Error state */
.form-field >>> .v-input--has-state .v-input__slot {
  border-color: #f87171 !important;
}

.form-field >>> .v-text-field--filled:not(.v-input--is-disabled).v-input--has-state > .v-input__control > .v-input__slot {
  border-color: #ef4444 !important;
}

.form-field >>> .v-messages__message {
  font-family: 'Lato', sans-serif;
  font-size: 0.75rem;
  color: #ef4444;
}

/* ============================================
   VUETIFY OVERRIDES
   ============================================ */
.v-btn {
  opacity: 1 !important;
}

.v-messages__message {
  color: #ef4444;
}

/* ============================================
   MOBILE RESPONSIVE
   ============================================ */
@media (max-width: 599px) {
  .claim-page {
    padding: 12px 8px !important;
    zoom: 1;
  }

  .claim-header {
    border-radius: 14px;
    margin-bottom: 16px;
  }

  .claim-header__top {
    flex-wrap: wrap;
    padding: 16px 16px 12px;
  }

  .back-btn {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    margin-right: 12px;
  }

  .claim-header__title {
    font-size: 1.05rem;
    white-space: normal;
  }

  .claim-header__actions {
    width: 100%;
    margin-left: 0;
    margin-top: 10px;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 4px;
  }

  .hero-action-btn {
    width: 36px;
    height: 36px;
  }

  .claim-sections {
    gap: 14px;
  }

  .content-grid--split {
    grid-template-columns: 1fr;
  }

  .section-card {
    border-radius: 14px;
    max-width: 100%;
    overflow: hidden;
  }

  .section-header {
    padding: 12px 16px;
  }

  .section-header__title {
    font-size: 0.65rem;
    letter-spacing: 1.5px;
  }

  .section-body {
    padding: 14px;
    overflow: hidden;
    word-break: break-word;
  }

  .form-row {
    flex-direction: column;
    gap: 2px;
    align-items: stretch;
  }

  .form-label {
    flex: none;
    width: 100%;
    font-size: 0.72rem;
    padding-top: 0;
    margin-bottom: 2px;
  }

  .form-field {
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box;
  }

  .form-field >>> .v-input {
    width: 100% !important;
  }

  .form-field >>> .v-input__control {
    width: 100% !important;
  }

  .form-field >>> .v-input__slot {
    width: 100% !important;
    box-sizing: border-box !important;
  }

  .file-card {
    flex-wrap: wrap;
  }

  .file-card__info {
    min-width: 0;
    width: calc(100% - 60px);
  }

  .toggle-card-group {
    flex-direction: column;
  }

  .toggle-card {
    padding: 12px 14px;
  }

  .toggle-card__icon-wrap {
    width: 32px;
    height: 32px;
    border-radius: 8px;
  }

  .toggle-card__icon {
    width: 16px;
    height: 16px;
  }

  .date-range {
    flex-direction: column;
    gap: 6px;
  }

  .date-range__separator {
    text-align: center;
  }

  .cn-cards-grid {
    grid-template-columns: 1fr;
  }

  .cn-detail-row {
    flex-direction: column;
    gap: 2px;
  }

  .cn-detail-label {
    flex: none;
  }

  .claim-confirm-card {
    padding: 24px 20px 20px;
  }

  .claim-confirm-card__title {
    font-size: 1.05rem;
  }

  .claim-page {
    overflow-x: hidden;
  }
}

/* Tablet breakpoint */
@media (min-width: 600px) and (max-width: 959px) {
  .claim-page {
    zoom: 1;
  }

  .claim-header__top {
    padding: 20px 20px 14px;
  }

  .claim-header__title {
    font-size: 1.15rem;
  }

  .claim-header__actions {
    flex-wrap: wrap;
    gap: 4px;
  }

  .content-grid--split {
    grid-template-columns: 1fr;
  }

  .section-body {
    padding: 18px;
  }

  .form-label {
    flex: 0 0 160px;
  }
}

/* ============================================
   DARK MODE
   ============================================ */
.dark-mode .claim-page {
  background-color: var(--app-page);
}

.dark-mode .claim-header {
  background: var(--app-card);
}

.dark-mode .claim-header__title {
  color: var(--app-text);
}

.dark-mode .back-btn {
  background: var(--app-form-tile);
  border-color: var(--app-form-tile-border);
}

.dark-mode .back-btn:hover {
  background: var(--app-form-tile-hover);
  border-color: var(--app-border-strong);
}

.dark-mode .section-card {
  background: var(--app-card);
}

.dark-mode .section-header {
  background: linear-gradient(135deg, var(--app-accent), var(--app-accent));
}

.dark-mode .section-header--amber {
  background: linear-gradient(135deg, var(--app-warning), var(--app-warning));
}

.dark-mode .section-header--purple {
  background: linear-gradient(135deg, var(--app-purple-fg), var(--app-purple-fg));
}

.dark-mode .section-body {
  background: var(--app-card);
}

.dark-mode .form-label {
  color: var(--app-text-muted);
}

.dark-mode .form-field >>> .v-text-field--filled > .v-input__control > .v-input__slot {
  border-color: var(--app-field-border) !important;
}

.dark-mode .form-field >>> .v-text-field--filled > .v-input__control > .v-input__slot:hover {
  background: var(--app-field-hover) !important;
  border-color: var(--app-field-border-hover) !important;
}

.dark-mode .form-field >>> .v-input--is-focused .v-input__slot {
  border-color: var(--app-accent) !important;
  box-shadow: 0 0 0 3px var(--app-ring) !important;
}

.dark-mode .form-field >>> .v-text-field input,
.dark-mode .form-field >>> .v-select__selection,
.dark-mode .form-field >>> .v-text-field textarea {
  color: var(--app-text);
}

.dark-mode .form-field >>> .v-label {
  color: var(--app-field-placeholder);
}

.dark-mode .form-field >>> .v-text-field--filled:not(.v-input--is-disabled) > .v-input__control > .v-input__slot {
  background: var(--app-field) !important;
  border: 1.5px solid var(--app-field-border) !important;
}

.dark-mode .form-field >>> .v-text-field--filled:not(.v-input--is-disabled) > .v-input__control > .v-input__slot:hover {
  border-color: var(--app-field-border-hover) !important;
}

.dark-mode .form-field >>> .v-input--is-disabled .v-input__slot {
  background: var(--app-field-disabled) !important;
  border: 1px dashed var(--app-field-disabled-border) !important;
}

.dark-mode .form-field >>> .v-input--is-disabled input,
.dark-mode .form-field >>> .v-input--is-disabled textarea,
.dark-mode .form-field >>> .v-input--is-disabled .v-select__selection {
  color: var(--app-text-faint) !important;
}

.dark-mode .hero-action-btn {
  background: var(--app-form-tile);
  color: var(--app-text-muted);
}

.dark-mode .hero-action-btn:hover {
  background: var(--app-form-tile-hover);
  color: var(--app-neutral-fg);
}

.dark-mode .hero-action-btn--green {
  background: var(--app-form-tile);
  color: var(--app-text);
}

.dark-mode .hero-action-btn--green:hover {
  background: var(--app-form-tile-hover);
  color: var(--app-text);
}

.dark-mode .hero-action-btn--green .v-icon {
  color: var(--app-text) !important;
}

.dark-mode .file-card {
  background: var(--app-form-tile);
}

.dark-mode .file-card:hover {
  background: var(--app-form-tile-hover);
}

.dark-mode .file-card__icon {
  background: var(--app-danger-bg);
  color: var(--app-danger-fg);
}

.dark-mode .file-card__name {
  color: var(--app-neutral-fg);
}

.dark-mode .file-card__hint {
  color: var(--app-text-faint);
}

.dark-mode .file-card__btn {
  background: var(--app-form-tile);
  color: var(--app-text-muted);
}

.dark-mode .file-card__btn:hover {
  background: var(--app-border);
  color: var(--app-neutral-fg);
}

.dark-mode .file-card__icon-wrap::after {
  border-color: var(--app-card);
}

.dark-mode .claim-copyable {
  background: var(--app-form-tile);
  border-color: var(--app-form-tile-border);
}

.dark-mode .claim-copyable:hover {
  background: var(--app-accent-soft);
  border-color: var(--app-accent);
}

.dark-mode .claim-copyable__text {
  color: var(--app-text-muted);
}

.dark-mode .claim-copyable:hover .claim-copyable__icon {
  color: var(--app-accent-strong);
}

.dark-mode .rv-datepicker__trigger {
  background: var(--app-field);
  border-color: var(--app-field-border);
}

.dark-mode .rv-datepicker__trigger:hover {
  border-color: var(--app-field-border-hover);
}

.dark-mode .rv-datepicker__text {
  color: var(--app-text);
}

.dark-mode .rv-datepicker__placeholder {
  color: var(--app-text-faint);
}

.dark-mode .toggle-card {
  background: var(--app-form-tile);
  border-color: var(--app-form-tile-border);
}

.dark-mode .toggle-card:hover {
  border-color: var(--app-border-strong);
  box-shadow: 0 2px 8px var(--app-shadow-soft);
}

.dark-mode .toggle-card--active-blue {
  background: var(--app-accent-soft);
  border-color: var(--app-accent);
  box-shadow: 0 0 0 3px var(--app-ring);
}

.dark-mode .toggle-card--active-red {
  background: var(--app-danger-bg);
  border-color: var(--app-danger-fg);
  box-shadow: 0 0 0 3px var(--app-ring-danger);
}

.dark-mode .toggle-card--active-purple {
  background: var(--app-purple-bg);
  border-color: var(--app-purple-fg);
  box-shadow: 0 0 0 3px var(--app-ring-purple);
}

.dark-mode .toggle-card__icon-wrap--blue {
  background: var(--app-accent-soft);
  color: var(--app-accent);
}

.dark-mode .toggle-card--active-blue .toggle-card__icon-wrap--blue {
  background: var(--app-accent);
  color: var(--app-accent-fg);
}

.dark-mode .toggle-card__icon-wrap--red {
  background: var(--app-danger-bg);
  color: var(--app-danger-fg);
}

.dark-mode .toggle-card--active-red .toggle-card__icon-wrap--red {
  background: var(--app-danger);
  color: var(--app-on-solid);
}

.dark-mode .toggle-card__icon-wrap--purple {
  background: var(--app-purple-bg);
  color: var(--app-purple-fg);
}

.dark-mode .toggle-card--active-purple .toggle-card__icon-wrap--purple {
  background: var(--app-purple-fg);
  color: var(--app-on-solid);
}

.dark-mode .toggle-card__title {
  color: var(--app-text);
}

.dark-mode .toggle-card__desc {
  color: var(--app-text-faint);
}

.dark-mode .toggle-card__check {
  border-color: var(--app-border-strong);
}

.dark-mode .section-header__badge {
  background: var(--app-veil);
}

.dark-mode .cn-card {
  background: var(--app-form-tile);
  border-color: var(--app-form-tile-border);
}

.dark-mode .cn-card:hover {
  border-color: var(--app-border);
}

.dark-mode .cn-description {
  color: var(--app-text-muted);
}

.dark-mode .cn-detail-label {
  color: var(--app-text-faint);
}

.dark-mode .cn-detail-value {
  color: var(--app-text);
}

.dark-mode .cn-detail-value--amount {
  color: var(--app-success-fg);
}

.dark-mode .claim-confirm-card {
  background: var(--app-form-tile);
}

.dark-mode .claim-confirm-card__title {
  color: var(--app-text);
}

.dark-mode .claim-confirm-card__message {
  color: var(--app-text-muted);
}

.dark-mode .claim-confirm-card__btn--cancel {
  background: var(--app-form-tile);
  color: var(--app-accent-strong);
  border-color: var(--app-form-tile-border);
}

.dark-mode .claim-confirm-card__btn--cancel:hover {
  background: var(--app-form-tile-hover);
  border-color: var(--app-accent-strong);
}

.dark-mode .claim-confirm-card__btn--confirm {
  background: var(--app-accent);
}

.dark-mode .claim-confirm-card__btn--confirm:hover {
  background: var(--app-accent);
}

/* >>> dark companions: the light rules above pin these to a dark ink
   with !important, which on the dark field renders the text invisible
   rather than absent. Token references only. <<< */

.dark-mode .form-field >>> .v-text-field input,
.dark-mode .form-field >>> .v-select__selection,
.dark-mode .form-field >>> .v-text-field textarea {
  color: var(--app-text) !important;
}
</style>

<style>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .v-picker__body {
  background: var(--app-card) !important;
}

.dark-mode .v-date-picker-header {
  background: var(--app-card) !important;
}

.dark-mode .v-date-picker-header__value button {
  color: var(--app-text) !important;
}

.dark-mode .v-date-picker-header .v-btn {
  color: var(--app-text-muted) !important;
}

.dark-mode .v-date-picker-header .v-btn:hover {
  background: var(--app-form-tile-hover) !important;
}

.dark-mode .v-date-picker-table thead th {
  color: var(--app-text-faint) !important;
}

.dark-mode .v-date-picker-table .v-btn {
  color: var(--app-text) !important;
}

.dark-mode .v-date-picker-table .v-btn:hover {
  background: var(--app-accent-soft) !important;
}

.dark-mode .v-date-picker-table .v-btn--disabled {
  color: var(--app-text-faint) !important;
}

.dark-mode .v-picker__actions {
  background: var(--app-form-tile) !important;
  border-top: 1px solid var(--app-border) !important;
}

/* >>> end auto-dark <<< */

/* ============================================
   VUETIFY DATE PICKER OVERRIDE (unscoped)
   ============================================ */
.v-picker {
  border-radius: 14px !important;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12) !important;
  border: none !important;
  overflow: hidden;
  font-family: 'Lato', sans-serif !important;
}

.v-picker .v-picker__body {
  border: none !important;
}

/* The unscoped `.v-menu__content …` block that used to sit here is gone.
   It was global — unscoped CSS is injected once and never removed, so a rule
   written for THIS page's dropdown repainted every menu in the app (with
   !important, and with hard-coded light hex) for the rest of the session. That
   is what made the same v-select look different depending on which page you had
   visited. Menus are defined ONCE, in tokens, in src/plugins/theme-glass.css §3
   — panel, list, item radius, hover, active, typography and the state overlay.
   A page that genuinely needs its own menu styling should give that v-menu a
   `content-class` and key off it, the way .bdt-menu and .tb-view-menu do. */

.v-picker__body {
  background: #fff !important;
}

.v-date-picker-header {
  padding: 12px 16px 8px !important;
  background: #fff !important;
}

.v-date-picker-header__value button {
  font-family: 'Lato', sans-serif !important;
  font-weight: 700 !important;
  font-size: 0.95rem !important;
  color: #1e293b !important;
}

.v-date-picker-header .v-btn {
  color: #64748b !important;
}

.v-date-picker-header .v-btn:hover {
  background: #f1f5f9 !important;
}

.v-date-picker-table thead th {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.72rem !important;
  font-weight: 600 !important;
  color: #94a3b8 !important;
  padding: 8px 0 !important;
}

.v-date-picker-table .v-btn {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.82rem !important;
  font-weight: 500 !important;
  color: #334155 !important;
}

.v-date-picker-table .v-btn:hover {
  background: #f0f9ff !important;
}

.v-date-picker-table .v-btn--active {
  background: #0ea5e9 !important;
  color: #fff !important;
  font-weight: 700 !important;
}

.v-date-picker-table .v-btn--active:hover {
  background: #0284c7 !important;
}

.v-date-picker-table .v-btn--outlined {
  border-color: #0ea5e9 !important;
  color: #0ea5e9 !important;
}

.v-date-picker-table .v-btn--disabled {
  color: #cbd5e1 !important;
}

.v-picker__actions {
  padding: 8px 16px 14px !important;
  background: #fff !important;
  border-top: 1px solid #f1f5f9 !important;
}

.v-picker__actions .v-btn {
  font-family: 'Lato', sans-serif !important;
  font-weight: 600 !important;
  font-size: 0.82rem !important;
  letter-spacing: 0.3px !important;
  text-transform: none !important;
  border-radius: 8px !important;
  padding: 0 16px !important;
}

.v-date-picker-years li,
.v-date-picker-months .v-btn {
  font-family: 'Lato', sans-serif !important;
}
</style>
